import React, { useState } from "react";

// ─── Constants ───────────────────────────────────────────────────
const BUSINESS_TYPES = [
  { value: "retail", label: "Retail" },
  { value: "wholesale", label: "Wholesale" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "services", label: "Services" },
  { value: "labor", label: "Labor" },
];

const BUSINESS_FIELDS = [
  "Plumber", "Electrician", "Carpenter", "Doctor", "Engineer",
  "Teacher", "Lawyer", "Accountant", "Architect", "Farmer",
  "Mechanic", "Tailor", "Shopkeeper", "Driver", "Painter",
  "Welder", "Mason", "Chef / Cook", "Pharmacist", "Jeweller",
];

const YES_NO = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

const OCC_TYPES = [
  { value: "job", label: "Job" },
  { value: "business", label: "Business" },
];

// ─── Shared small UI pieces ─────────────────────────────────────
function Radio({ label, name, value, options, onChange, isEditing, displayValue }) {
  if (!isEditing) {
    return (
      <div>
        <label className="font-semibold text-sm text-gray-700">{label}</label>
        <p className="text-gray-800 mt-1">{displayValue || value || "—"}</p>
      </div>
    );
  }
  return (
    <div>
      <label className="font-semibold text-sm text-gray-700 block mb-1">{label}</label>
      <div className="flex gap-4 mt-1">
        {options.map((opt) => (
          <label key={opt.value} className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${value === opt.value ? "border-purple-500 bg-purple-50 text-purple-700" : "border-gray-200 hover:border-gray-300"}`}>
            <input type="radio" name={name} value={opt.value} checked={value === opt.value} onChange={(e) => onChange(name, e.target.value)} className="accent-purple-600" />
            <span className="text-sm font-medium">{opt.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function Field({ label, name, value, onChange, isEditing, multiline }) {
  if (!isEditing) {
    return (
      <div>
        <label className="font-semibold text-sm text-gray-700">{label}</label>
        <p className="text-gray-800 mt-1">{value || "—"}</p>
      </div>
    );
  }
  return (
    <div>
      <label className="font-semibold text-sm text-gray-700">{label}</label>
      {multiline ? (
        <textarea name={name} value={value || ""} onChange={(e) => onChange(name, e.target.value)} rows={3} className="w-full mt-1 border rounded px-3 py-2 text-sm resize-none" />
      ) : (
        <input name={name} value={value || ""} onChange={(e) => onChange(name, e.target.value)} className="w-full mt-1 border rounded px-3 py-2" />
      )}
    </div>
  );
}

function BusinessFieldPicker({ value, onChange, isEditing, label = "Business Field", name = "business_field" }) {
  const isCustom = value && !BUSINESS_FIELDS.includes(value);
  const [showCustom, setShowCustom] = useState(isCustom);

  const handleSelect = (v) => {
    if (v === "__custom__") { setShowCustom(true); onChange(name, ""); }
    else { setShowCustom(false); onChange(name, v); }
  };

  if (!isEditing) {
    return (
      <div>
        <label className="font-semibold text-sm text-gray-700">{label}</label>
        <p className="text-gray-800 mt-1">{value || "—"}</p>
      </div>
    );
  }
  return (
    <div>
      <label className="font-semibold text-sm text-gray-700">{label}</label>
      <select value={showCustom || isCustom ? "__custom__" : value || ""} onChange={(e) => handleSelect(e.target.value)} className="w-full mt-1 border rounded px-3 py-2">
        <option value="">Select field</option>
        {BUSINESS_FIELDS.map((f) => <option key={f} value={f}>{f}</option>)}
        <option value="__custom__">Other (Custom)</option>
      </select>
      {(showCustom || isCustom) && (
        <input type="text" value={value || ""} onChange={(e) => onChange(name, e.target.value)} placeholder="Enter custom field" className="w-full mt-1 border rounded px-3 py-2" />
      )}
    </div>
  );
}

function BusinessTypePicker({ value, onChange, isEditing }) {
  if (!isEditing) {
    return (
      <div>
        <label className="font-semibold text-sm text-gray-700">Business Type</label>
        <p className="text-gray-800 mt-1">{BUSINESS_TYPES.find((t) => t.value === value)?.label || "—"}</p>
      </div>
    );
  }
  return (
    <div>
      <label className="font-semibold text-sm text-gray-700">Business Type</label>
      <select value={value || ""} onChange={(e) => onChange("business_type", e.target.value)} className="w-full mt-1 border rounded px-3 py-2">
        <option value="">Select type</option>
        {BUSINESS_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
      </select>
    </div>
  );
}

// ─── Reusable Job/Business detail block (used for user + each child) ─
function OccupationDetail({ data, onChange, isEditing, borderColor = "blue" }) {
  const colorMap = { blue: "border-blue-300", green: "border-green-300" };

  if (data.occupation_type === "job") {
    return (
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 pl-2 border-l-4 ${colorMap.blue}`}>
        <Field label="Company Name" name="job_company_name" value={data.job_company_name} onChange={onChange} isEditing={isEditing} />
        <Field label="Role / Position" name="job_role" value={data.job_role} onChange={onChange} isEditing={isEditing} />
        <BusinessFieldPicker value={data.job_field} onChange={onChange} isEditing={isEditing} label="Job Field" name="job_field" />
        <Field label="About Company" name="job_company_about" value={data.job_company_about} onChange={onChange} isEditing={isEditing} multiline />
        <Field label="Company Address" name="job_company_address" value={data.job_company_address} onChange={onChange} isEditing={isEditing} multiline />
      </div>
    );
  }

  if (data.occupation_type === "business") {
    return (
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 pl-2 border-l-4 ${colorMap.green}`}>
        <Field label="Business Name" name="business_name" value={data.business_name} onChange={onChange} isEditing={isEditing} />
        <BusinessTypePicker value={data.business_type} onChange={onChange} isEditing={isEditing} />
        <BusinessFieldPicker value={data.business_field} onChange={onChange} isEditing={isEditing} />
        <Field label="About Business" name="business_about" value={data.business_about} onChange={onChange} isEditing={isEditing} multiline />
        <div className="md:col-span-2">
          <Field label="Business Address" name="business_address" value={data.business_address} onChange={onChange} isEditing={isEditing} multiline />
        </div>
      </div>
    );
  }
  return null;
}

// Clear opposite occupation fields on switch
function clearOccupation(onChange, keepType) {
  const jobFields = ["job_company_name", "job_role", "job_field", "job_company_about", "job_company_address"];
  const bizFields = ["business_name", "business_type", "business_field", "business_about", "business_address"];
  const toClear = keepType === "job" ? bizFields : jobFields;
  toClear.forEach((f) => onChange(f, ""));
}

// ─── INSURANCE SECTION ──────────────────────────────────────────
export function InsuranceSection({ data, onChange, isEditing }) {
  const handleChange = (name, value) => {
    onChange(name, value);
    if (value === "no") {
      if (name === "medical_insurance") onChange("medical_insurance_company", "");
      if (name === "accident_insurance") onChange("accident_insurance_company", "");
    }
  };

  return (
    <div className="border rounded-lg p-4 bg-gray-50">
      <h3 className="text-md font-semibold mb-4 text-gray-800">🛡️ Insurance Details</h3>
      <div className="space-y-4">
        <Radio label="Medical Insurance" name="medical_insurance" value={data.medical_insurance || "no"} options={YES_NO} onChange={handleChange} isEditing={isEditing} displayValue={data.medical_insurance === "yes" ? "Yes" : "No"} />
        {data.medical_insurance === "yes" && (
          <Field label="Insurance Company" name="medical_insurance_company" value={data.medical_insurance_company} onChange={onChange} isEditing={isEditing} />
        )}
        <Radio label="Accident Insurance" name="accident_insurance" value={data.accident_insurance || "no"} options={YES_NO} onChange={handleChange} isEditing={isEditing} displayValue={data.accident_insurance === "yes" ? "Yes" : "No"} />
        {data.accident_insurance === "yes" && (
          <Field label="Insurance Company" name="accident_insurance_company" value={data.accident_insurance_company} onChange={onChange} isEditing={isEditing} />
        )}
      </div>
    </div>
  );
}

// ─── OCCUPATION SECTION ─────────────────────────────────────────
export function OccupationSection({ data, onChange, isEditing }) {
  const handleTypeChange = (name, value) => {
    onChange(name, value);
    clearOccupation(onChange, value);
  };

  return (
    <div className="border rounded-lg p-4 bg-gray-50">
      <h3 className="text-md font-semibold mb-4 text-gray-800">💼 Occupation Details</h3>
      <div className="space-y-4">
        <Radio label="Occupation Type" name="occupation_type" value={data.occupation_type || ""} options={OCC_TYPES} onChange={handleTypeChange} isEditing={isEditing} displayValue={data.occupation_type === "job" ? "Job" : data.occupation_type === "business" ? "Business" : "—"} />
        <OccupationDetail data={data} onChange={onChange} isEditing={isEditing} />
      </div>
    </div>
  );
}

// ─── CHILDREN SECTION ───────────────────────────────────────────
const EMPTY_CHILD = { name: "", occupation_type: "", job_company_name: "", job_role: "", job_field: "", job_company_about: "", job_company_address: "", business_name: "", business_type: "", business_field: "", business_about: "", business_address: "" };

function parseChildren(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; } catch { return []; }
}

export function ChildrenSection({ data, onChange, isEditing }) {
  const children = parseChildren(data.children_details);

  const save = (updated) => onChange("children_details", JSON.stringify(updated));
  const addChild = () => save([...children, { ...EMPTY_CHILD }]);
  const removeChild = (i) => save(children.filter((_, idx) => idx !== i));

  const updateChild = (index, field, value) => {
    const updated = [...children];
    updated[index] = { ...updated[index], [field]: value };
    if (field === "occupation_type") {
      const jobFields = ["job_company_name", "job_role", "job_field", "job_company_about", "job_company_address"];
      const bizFields = ["business_name", "business_type", "business_field", "business_about", "business_address"];
      (value === "job" ? bizFields : jobFields).forEach((f) => { updated[index][f] = ""; });
    }
    save(updated);
  };

  const handleToggle = (name, value) => {
    onChange(name, value);
    if (value === "no") onChange("children_details", null);
  };

  return (
    <div className="border rounded-lg p-4 bg-gray-50">
      <h3 className="text-md font-semibold mb-4 text-gray-800">👨‍👩‍👧‍👦 Children Details</h3>
      <Radio label="Do you have children?" name="has_children" value={data.has_children || "no"} options={YES_NO} onChange={handleToggle} isEditing={isEditing} displayValue={data.has_children === "yes" ? "Yes" : "No"} />

      {data.has_children === "yes" && (
        <div className="mt-4 space-y-4">
          {children.map((child, i) => (
            <div key={i} className="border rounded-lg p-3 bg-white">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-semibold text-sm text-purple-700">Child {i + 1}</h4>
                {isEditing && children.length > 1 && (
                  <button type="button" onClick={() => removeChild(i)} className="text-red-500 hover:text-red-700 text-sm px-2">✕ Remove</button>
                )}
              </div>
              <div className="space-y-3">
                <Field label="Name" name="name" value={child.name} onChange={(n, v) => updateChild(i, n, v)} isEditing={isEditing} />
                <Radio label="Occupation" name="occupation_type" value={child.occupation_type || ""} options={OCC_TYPES} onChange={(n, v) => updateChild(i, n, v)} isEditing={isEditing} displayValue={child.occupation_type === "job" ? "Job" : child.occupation_type === "business" ? "Business" : "—"} />
                <OccupationDetail data={child} onChange={(n, v) => updateChild(i, n, v)} isEditing={isEditing} />
              </div>
            </div>
          ))}
          {isEditing && (
            <button type="button" onClick={addChild} className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-purple-400 hover:text-purple-600 transition-colors">+ Add Child</button>
          )}
          {!isEditing && children.length === 0 && <p className="text-gray-500 text-sm">No children details added.</p>}
        </div>
      )}
    </div>
  );
}